import React, { useState, useMemo } from 'react';
import {
    Button,
    Badge,
    Group,
    Paper,
    ActionIcon,
    Skeleton,
    Anchor,
    Flex,
    Modal,
    Stack,
    Text,
    Loader,
    // NativeSelect — used only by the (now hidden) per-card Location selector.
    // MultiSelect — used only by the (now hidden) per-card Tags selector.
    Select,
    Tooltip,
    SimpleGrid,
    UnstyledButton,
    ThemeIcon,
    Box,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
// updateDevice is only used by the (now hidden) Location/Tags editors.
import { listDeviceFetcher, Device, unclaimDevice, type DeviceLatestPing } from '../../data/Device';
import { AxiosError } from 'axios';
import { IconTrash, IconPencil, IconPlus, IconArrowRight, IconEye } from '@tabler/icons-react';
import { useCsrf } from '../../hooks/useCsrf';
import { track } from '../../utils/mixpanel';
import { AnalyticsEvents } from '../../analytics/events';
import PlaylistPreviewModal from './PlaylistPreviewModal';
import NewSceneDrawer from '../shell/studio/NewSceneDrawer';
import { notifications } from '@mantine/notifications';
// Tags UI is hidden for now — not using it quite yet.
// import { listDeviceTagFetcher } from '../../data/DeviceTag';
// import type { DeviceTag } from '../../data/DeviceTag';
// Location UI is hidden for now — not using it quite yet.
// import { listLocationFetcher, Location } from '../../data/Location';
import {
    listComponents,
    listPlaylists,
    listScenes,
    updateDeviceV2Config,
    updatePlaylist,
    type PlaylistSceneEntry,
    type SceneTemplateId,
    type ShellComponent,
} from '../../data/ShellConfig';
import { useAuth } from '../../hooks/useAuth';
import LiveBadge from './LiveBadge';
import ScenePreviewFrame from '../shell/studio/ScenePreviewFrame';
import AddSceneDrawer from '../shell/studio/AddSceneDrawer';

/** Default duration for a scene appended to a playlist from the preview. */
const PREVIEW_SCENE_DURATION = 60;

const defaultDevice = {
    id: '',
    name: '',
};

/** What the device most recently reported it was displaying. */
const describePingContent = (ping: DeviceLatestPing): string => {
    if (ping.isCommercial) return 'Commercial break';
    if (ping.currentSport) return ping.currentSport;
    if (ping.imageRoute) return ping.imageRoute;
    return 'Idle';
};

const formatPingTimestamp = (isoString: string): string =>
    new Date(isoString).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });

/**
 * A spoof of the device's landscape "main" screen, sitting left of the portrait
 * side-screen strip. Shows what the device most recently pinged as displaying,
 * with the ping timestamp below it.
 */
const MainScreenSpoof: React.FC<{ ping: DeviceLatestPing | null | undefined }> = ({ ping }) => (
    <Box
        style={{
            // The main screen sets the row height via its 16:9 ratio; the side
            // screen stretches to match. Grow factors (160:51 = the two screens'
            // widths at equal height) fill the card width.
            flex: '160 1 0',
            minWidth: 0,
            aspectRatio: '16 / 9',
            borderRadius: 6,
            overflow: 'hidden',
            background: 'var(--mantine-color-dark-6)',
            color: 'var(--mantine-color-white)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            padding: 8,
            textAlign: 'center',
        }}
    >
        {ping ? (
            <>
                <Text size="sm" fw={600} lineClamp={2}>
                    {describePingContent(ping)}
                </Text>
                <Text size="xs" c="dimmed">
                    {formatPingTimestamp(ping.createdAt)}
                </Text>
            </>
        ) : (
            <Text size="xs" c="dimmed">
                No pings yet
            </Text>
        )}
    </Box>
);

/** One small, read-only scene preview tile in a device card's playlist strip. */
const ScenePreviewTile: React.FC<{ entry: PlaylistSceneEntry; components: ShellComponent[] }> = ({
    entry,
    components,
}) => {
    const panels = entry.scene?.panels;
    if (!panels?.length) return null;
    return (
        <Box
            style={{
                // Paired with the 16:9 main screen's 160 grow factor (see MainScreenSpoof).
                // No aspect-ratio here: the height comes from stretching to the main
                // screen's height, which avoids an aspect-ratio/flex feedback loop.
                flex: '51 1 0',
                minWidth: 0,
                alignSelf: 'stretch',
                borderRadius: 6,
                overflow: 'hidden',
                background: 'var(--mantine-color-dark-6)',
            }}
        >
            <ScenePreviewFrame
                variant="tile"
                templateId={(entry.scene?.templateId ?? 'full') as SceneTemplateId}
                panels={panels.map(panel => ({
                    componentId: panel.componentId,
                    propOverrides: panel.propOverrides ?? [],
                }))}
                components={components}
            />
        </Box>
    );
};

const Devices: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { csrfToken } = useCsrf();
    const { mutate: globalMutate } = useSWRConfig();
    const [unclaimOpened, { open: openUnclaim, close: closeUnclaim }] = useDisclosure(false);
    const [activeDevice, setActiveDevice] = useState(defaultDevice);
    const [isDeleting, setIsDeleting] = useState(false);
    // A pending playlist switch awaiting confirmation (only when replacing/removing
    // a playlist the device is already running).
    const [pendingPlaylistChange, setPendingPlaylistChange] = useState<{
        device: Device;
        playlistId: string | null;
    } | null>(null);
    // The device whose playlist is being previewed full-screen, if any.
    const [previewDevice, setPreviewDevice] = useState<Device | null>(null);
    // The scene being edited in place (drawer overlaid on the preview — no route change).
    const [editSceneId, setEditSceneId] = useState<string | null>(null);
    // True while a scene edit revalidates, to overlay the preview with "Updating…".
    const [previewUpdating, setPreviewUpdating] = useState(false);
    // The "Add scene" picker / new-scene builder, opened from the preview's trailing slide.
    const [addSceneOpen, setAddSceneOpen] = useState(false);
    const [newSceneOpen, setNewSceneOpen] = useState(false);
    // Location UI hidden for now — not using it quite yet.
    // const [locationFilter, setLocationFilter] = useState<string>('all');

    const handleNav = (route: string) => {
        navigate(route);
    };

    const {
        data: listDevicesResponse,
        isLoading,
        error,
        mutate,
    } = useSWR<Device[], AxiosError>('api/devices', listDeviceFetcher, { refreshInterval: 30_000 });

    // Tags UI hidden for now — not using it quite yet.
    // const { data: listDeviceTagsResponse } = useSWR<DeviceTag[], AxiosError>('api/device-tags', listDeviceTagFetcher);

    // Location UI hidden for now — not using it quite yet.
    // const { data: listLocationsResponse } = useSWR<Location[], AxiosError>('api/locations', listLocationFetcher);

    // For the per-card playlist preview: playlists carry their scenes+panels, and
    // components resolve each panel's media (see PlaylistPreviewStrip).
    const { data: playlists = [] } = useSWR('api/playlists', listPlaylists);
    const { data: allScenes = [] } = useSWR('api/scenes', listScenes);
    const { data: components = [] } = useSWR('api/components', listComponents);

    const playlistById = useMemo(
        () => new Map(playlists.map(playlist => [playlist.id, playlist])),
        [playlists]
    );

    const playlistSelectData = useMemo(
        () => playlists.map(playlist => ({ value: playlist.id, label: playlist.name })),
        [playlists]
    );

    // Location UI hidden for now — not using it quite yet.
    // const locationSelectData = useMemo(() => {
    //     const base = listLocationsResponse?.map(l => ({ value: l.id, label: l.name })) ?? [];
    //     return [{ value: '', label: '— No location —' }, ...base];
    // }, [listLocationsResponse]);
    //
    // const locationFilterData = useMemo(() => {
    //     const base = listLocationsResponse?.map(l => ({ value: l.id, label: l.name })) ?? [];
    //     return [{ value: 'all', label: 'All locations' }, { value: 'unassigned', label: 'Unassigned' }, ...base];
    // }, [listLocationsResponse]);

    // Location filtering is hidden for now, so show every device.
    const filteredDevices = listDevicesResponse ?? [];

    // Tags UI hidden for now — not using it quite yet.
    // const tagSelectData = useMemo(
    //     () => listDeviceTagsResponse?.map(t => ({ value: t.id, label: t.label })) ?? [],
    //     [listDeviceTagsResponse]
    // );

    const handleUnclaim = (device: Device) => {
        setActiveDevice(device);
        openUnclaim();
    };

    const handleUnclaimConfirmation = async () => {
        setIsDeleting(true);
        await unclaimDevice(csrfToken, activeDevice?.id);
        setIsDeleting(false);
        closeUnclaim();
        notifications.show({
            title: 'Device Removed',
            message: `Successfully removed device ${activeDevice?.name}.`,
        });
        setActiveDevice(defaultDevice);
        await mutate();
    };

    // Location UI hidden for now — not using it quite yet.
    // const handleLocationChange = async (deviceId: string, locationId: string) => {
    //     try {
    //         await updateDevice(csrfToken, deviceId, {
    //             locationId: locationId === '' ? null : locationId,
    //         });
    //         await mutate();
    //         notifications.show({
    //             title: 'Location updated',
    //             message: 'Device location saved.',
    //         });
    //     } catch (err: unknown) {
    //         notifications.show({
    //             title: 'Update failed',
    //             message: err instanceof Error ? err.message : 'Unknown error',
    //         });
    //     }
    // };

    const applyPlaylistChange = async (deviceId: string, playlistId: string | null) => {
        try {
            await updateDeviceV2Config(deviceId, playlistId, csrfToken);
            track(AnalyticsEvents.PlaylistUpdated, {
                source: 'device',
                action: playlistId ? 'assign' : 'remove',
                via: 'device_card',
                playlistId,
            });
            await mutate();
            const name = playlistId ? playlistById.get(playlistId)?.name : null;
            notifications.show({
                title: 'Playlist updated',
                message: name ? `Device is now playing “${name}”.` : 'Playlist removed from device.',
            });
        } catch (err: unknown) {
            notifications.show({
                title: 'Update failed',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        }
    };

    // Confirm before disrupting a device that's already running a playlist; a fresh
    // assignment to an unassigned device applies straight away.
    const requestPlaylistChange = (device: Device, playlistId: string | null) => {
        if (playlistId === (device.playlistId ?? null)) return;
        if (device.playlistId) {
            setPendingPlaylistChange({ device, playlistId });
            return;
        }
        void applyPlaylistChange(device.id, playlistId);
    };

    const confirmPlaylistChange = async () => {
        if (!pendingPlaylistChange) return;
        await applyPlaylistChange(pendingPlaylistChange.device.id, pendingPlaylistChange.playlistId);
        setPendingPlaylistChange(null);
    };

    // Appends a scene to the currently-previewed device's playlist (from the
    // preview's trailing "Add scene" slide), then overlays the preview with
    // "Updating…" while it revalidates. The preview lands on the new scene
    // because its internal index was already sitting on the add slot.
    const appendSceneToPlaylist = async (sceneId: string, method: 'existing' | 'new') => {
        const playlistId = previewDevice?.playlistId;
        const target = playlistId ? playlistById.get(playlistId) : undefined;
        if (!playlistId || !target) return;
        const existing = (target.playlistScenes ?? []).map(entry => ({
            sceneId: entry.sceneId,
            durationSeconds: entry.durationSeconds,
        }));
        setPreviewUpdating(true);
        try {
            await updatePlaylist(
                playlistId,
                {
                    name: target.name,
                    scenes: [...existing, { sceneId, durationSeconds: PREVIEW_SCENE_DURATION }],
                },
                csrfToken,
            );
            track(AnalyticsEvents.PlaylistUpdated, {
                source: 'device_preview',
                action: 'add_scene',
                method,
                playlistId,
            });
            await Promise.all([globalMutate('api/playlists'), globalMutate('api/scenes')]);
        } catch {
            notifications.show({ color: 'red', message: 'Could not add the scene.' });
        } finally {
            setPreviewUpdating(false);
        }
    };

    // Tags UI hidden for now — not using it quite yet.
    // const handleTagsChange = async (deviceId: string, tagIds: string[]) => {
    //     try {
    //         await updateDevice(csrfToken, deviceId, { deviceTagIds: tagIds });
    //         await mutate();
    //         notifications.show({
    //             title: 'Tags updated',
    //             message: 'Device tags saved.',
    //         });
    //     } catch (err: unknown) {
    //         notifications.show({
    //             title: 'Update failed',
    //             message: err instanceof Error ? err.message : 'Unknown error',
    //         });
    //     }
    // };

    const loadingIndicator = (
        <div>
            <Flex justify={'space-between'} align={'center'} style={{ marginTop: '16px', marginBottom: '16px' }}>
                <Skeleton height={38} width="100" radius="md" />
                <Skeleton height={32} width="100" radius="md" />
            </Flex>
            <Stack>
                <Skeleton height={76} width="100%" radius="md" />
                <Skeleton height={76} width="100%" radius="md" />
                <Skeleton height={76} width="100%" radius="md" />
            </Stack>
        </div>
    );

    if (isLoading) {
        return loadingIndicator;
    }
    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <Modal opened={unclaimOpened} onClose={closeUnclaim} title="Remove device from account">
                <Stack gap="sm">
                    <Text style={{ marginBottom: '16px' }}>
                        Are you sure you want to remove device{' '}
                        <Text span fw={700}>
                            {activeDevice?.name}
                        </Text>{' '}
                        from your account?
                    </Text>
                    <Button color="red" onClick={handleUnclaimConfirmation} fullWidth>
                        {isDeleting ? <Loader color="gray" size={18} /> : 'Confirm Delete'}
                    </Button>
                    <Button color="gray" onClick={closeUnclaim} fullWidth>
                        Cancel
                    </Button>
                </Stack>
            </Modal>
            <Modal
                opened={pendingPlaylistChange !== null}
                onClose={() => setPendingPlaylistChange(null)}
                title="Switch playlist?"
                centered
            >
                {pendingPlaylistChange && (
                    <>
                        <Group gap="sm" wrap="nowrap" align="center" mb="md">
                            <Badge variant="light" color="gray" size="lg" tt="none">
                                {(pendingPlaylistChange.device.playlistId &&
                                    playlistById.get(pendingPlaylistChange.device.playlistId)?.name) ??
                                    'A playlist'}
                            </Badge>
                            <IconArrowRight
                                size={18}
                                color="var(--mantine-color-dimmed)"
                                style={{ flexShrink: 0 }}
                            />
                            <Badge variant="light" size="lg" tt="none">
                                {pendingPlaylistChange.playlistId
                                    ? (playlistById.get(pendingPlaylistChange.playlistId)?.name ??
                                      'Selected playlist')
                                    : 'No playlist'}
                            </Badge>
                        </Group>
                        <Text size="sm" mb="lg">
                            “{pendingPlaylistChange.device.name}” is currently playing “
                            {(pendingPlaylistChange.device.playlistId &&
                                playlistById.get(pendingPlaylistChange.device.playlistId)?.name) ??
                                'a playlist'}
                            ”.{' '}
                            {pendingPlaylistChange.playlistId
                                ? `Switch it to “${
                                      playlistById.get(pendingPlaylistChange.playlistId)?.name ??
                                      'the selected playlist'
                                  }”?`
                                : 'Remove its playlist? The screen will stop playing it.'}
                        </Text>
                        <Flex justify="flex-end" gap="sm">
                            <Button variant="default" onClick={() => setPendingPlaylistChange(null)}>
                                Cancel
                            </Button>
                            <Button onClick={confirmPlaylistChange}>
                                {pendingPlaylistChange.playlistId ? 'Switch playlist' : 'Remove playlist'}
                            </Button>
                        </Flex>
                    </>
                )}
            </Modal>
            <PlaylistPreviewModal
                opened={previewDevice !== null}
                onClose={() => setPreviewDevice(null)}
                playlistName={
                    previewDevice?.playlistId
                        ? playlistById.get(previewDevice.playlistId)?.name
                        : undefined
                }
                scenes={
                    previewDevice?.playlistId
                        ? playlistById.get(previewDevice.playlistId)?.playlistScenes ?? []
                        : []
                }
                components={components}
                updating={previewUpdating}
                // Keep the preview open underneath; the drawer stacks over it.
                onEditScene={sceneId => {
                    track(AnalyticsEvents.SceneEditOpened, {
                        sceneId,
                        source: 'device_preview',
                    });
                    setEditSceneId(sceneId);
                }}
                onAddScene={() => {
                    track(AnalyticsEvents.SceneAddOpened, { source: 'device_preview' });
                    setAddSceneOpen(true);
                }}
            />
            <NewSceneDrawer
                opened={editSceneId !== null}
                sceneId={editSceneId ?? undefined}
                onClose={() => setEditSceneId(null)}
                components={components}
                // Above the full-screen preview modal (Mantine's default is 200).
                zIndex={300}
                onSaved={() => {
                    // Close the drawer so the preview is visible, then overlay it
                    // with "Updating…" while the edit revalidates. A scene edit
                    // ripples into the playlists (and the card/preview strips) that
                    // use it, so revalidate both before revealing the fresh UI.
                    setEditSceneId(null);
                    setPreviewUpdating(true);
                    void (async () => {
                        try {
                            await Promise.all([
                                globalMutate('api/scenes'),
                                globalMutate('api/playlists'),
                            ]);
                        } finally {
                            setPreviewUpdating(false);
                            notifications.show({
                                title: 'Scene updated',
                                message: 'Your changes have been saved.',
                            });
                        }
                    })();
                }}
            />
            {/* Add-scene picker, opened from the preview's trailing slide. */}
            <AddSceneDrawer
                opened={addSceneOpen}
                onClose={() => setAddSceneOpen(false)}
                scenes={allScenes}
                zIndex={300}
                onPick={sceneId => {
                    setAddSceneOpen(false);
                    void appendSceneToPlaylist(sceneId, 'existing');
                }}
                onNewScene={() => {
                    setAddSceneOpen(false);
                    setNewSceneOpen(true);
                }}
            />
            {/* Build a brand-new scene, then append it to the previewed playlist. */}
            <NewSceneDrawer
                opened={newSceneOpen}
                onClose={() => setNewSceneOpen(false)}
                components={components}
                zIndex={300}
                onSaved={scene => {
                    setNewSceneOpen(false);
                    void appendSceneToPlaylist(scene.id, 'new');
                }}
            />
            <Flex
                direction={{ base: 'column', sm: 'row' }}
                justify="space-between"
                align={{ base: 'stretch', sm: 'center' }}
                gap="sm"
            >
                <h2 style={{ margin: 0 }}>My Screens</h2>
                <Flex justify={{ base: 'stretch', sm: 'flex-end' }} gap="sm" wrap="wrap">
                    <Button onClick={() => handleNav('/device/claim')} size={'xs'}>
                        Claim a Device
                    </Button>
                    {(user?.name === 'Evan Hsu' || user?.name === 'Scott Sword') && (
                        <Button onClick={() => handleNav('/devices/new')} size={'xs'}>
                            Register a new Device (Admin)
                        </Button>
                    )}
                </Flex>
            </Flex>
            {/* Location UI hidden for now — not using it quite yet.
            <Flex align="center" gap="sm" style={{ marginBottom: '16px' }}>
                <Select
                    placeholder="Filter by location"
                    data={locationFilterData}
                    value={locationFilter}
                    onChange={v => setLocationFilter(v ?? 'all')}
                    size="sm"
                    style={{ width: 220 }}
                    allowDeselect={false}
                />
                {locationFilter !== 'all' && (
                    <Text size="sm" c="dimmed">
                        {filteredDevices.length} device{filteredDevices.length !== 1 ? 's' : ''}
                    </Text>
                )}
            </Flex>
            */}
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" mt="md">
                {filteredDevices.map(device => (
                    <Paper
                        key={device?.id}
                        shadow="xs"
                        p="lg"
                        radius="md"
                        withBorder
                        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                    >
                        <Flex justify="space-between" align="flex-start" gap="xs">
                            <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                                <Flex align="center" gap={4} style={{ minWidth: 0 }}>
                                    <Anchor
                                        onClick={() => handleNav(`/device/${device.id}`)}
                                        underline="hover"
                                        style={{ minWidth: 0 }}
                                    >
                                        <Text fw={600} size="lg" truncate inherit>
                                            {device?.name}
                                        </Text>
                                    </Anchor>
                                    <Tooltip label="Edit device name" withArrow>
                                        <ActionIcon
                                            variant="subtle"
                                            color="gray"
                                            size="sm"
                                            aria-label="Edit device name"
                                            onClick={() => handleNav(`/device/${device.id}/edit`)}
                                        >
                                            <IconPencil style={{ width: '70%', height: '70%' }} stroke={1.5} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Flex>
                                <LiveBadge latestPing={device.latestPing} />
                            </Stack>
                            <Tooltip label="Remove from account" withArrow>
                                <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    aria-label="Remove from account"
                                    onClick={() => handleUnclaim(device)}
                                >
                                    <IconTrash style={{ width: '70%', height: '70%' }} stroke={1.5} />
                                </ActionIcon>
                            </Tooltip>
                        </Flex>
                        <Stack gap={4}>
                            {/* Screen preview: spoof main screen (left) + a single side-screen scene (right). */}
                            <Flex gap="xs" align="stretch">
                                <MainScreenSpoof ping={device.latestPing} />
                                {(() => {
                                    const firstScene = device.playlistId
                                        ? playlistById.get(device.playlistId)?.playlistScenes?.[0]
                                        : undefined;
                                    return firstScene ? (
                                        <ScenePreviewTile
                                            entry={firstScene}
                                            components={components}
                                        />
                                    ) : null;
                                })()}
                            </Flex>
                            {/* Playlist label now sits with its dropdown, below the preview. */}
                            <Text size="xs" c="dimmed" fw={500} tt="uppercase" mt="sm">
                                Current Playlist
                            </Text>
                            <Select
                                placeholder="No playlist"
                                data={playlistSelectData}
                                value={device.playlistId ?? null}
                                onChange={value => device?.id && requestPlaylistChange(device, value)}
                                size="sm"
                                searchable
                                clearable
                            />
                            {/* Opens the full-screen, swipeable scene preview. */}
                            <Button
                                mt="xs"
                                variant="light"
                                size="xs"
                                fullWidth
                                leftSection={<IconEye size="0.9rem" stroke={1.8} />}
                                disabled={
                                    !(
                                        device.playlistId &&
                                        (playlistById.get(device.playlistId)?.playlistScenes?.length ?? 0) > 0
                                    )
                                }
                                onClick={() => {
                                    track(AnalyticsEvents.ScreenPreviewOpened, {
                                        deviceId: device.id,
                                        playlistId: device.playlistId ?? null,
                                        sceneCount:
                                            (device.playlistId &&
                                                playlistById.get(device.playlistId)?.playlistScenes
                                                    ?.length) ||
                                            0,
                                    });
                                    setPreviewDevice(device);
                                }}
                            >
                                Preview
                            </Button>
                        </Stack>
                        {/* Tags UI hidden for now — not using it quite yet.
                        {listDeviceTagsResponse?.length === 0 ? (
                            <Text size="sm">
                                No device tags yet.{' '}
                                <Anchor size="sm" onClick={() => handleNav(`/device-tags/new`)}>
                                    Create one
                                </Anchor>
                            </Text>
                        ) : (
                            <Stack gap={4}>
                                <Text size="xs" c="dimmed" fw={500} tt="uppercase">
                                    Tags
                                </Text>
                                <MultiSelect
                                    placeholder="Device tags"
                                    data={tagSelectData}
                                    value={device.deviceTags?.map(t => t.id) ?? []}
                                    onChange={vals => device?.id && handleTagsChange(device.id, vals)}
                                    size="sm"
                                    clearable
                                />
                            </Stack>
                        )}
                        */}
                        {/* Location UI hidden for now — not using it quite yet.
                        <Stack gap={4}>
                            <Text size="xs" c="dimmed" fw={500} tt="uppercase">
                                Location
                            </Text>
                            <NativeSelect
                                onChange={event =>
                                    device?.id && handleLocationChange(device.id, event.currentTarget.value)
                                }
                                size="sm"
                                value={device.locationId ?? ''}
                                data={locationSelectData}
                            />
                        </Stack>
                        */}
                    </Paper>
                ))}
                <UnstyledButton
                    onClick={() => handleNav('/device/claim')}
                    style={{
                        border: '2px dashed var(--mantine-color-default-border)',
                        borderRadius: 'var(--mantine-radius-md)',
                        padding: '32px 16px',
                        minHeight: 200,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                        background: 'var(--mantine-color-default-hover)',
                    }}
                >
                    <ThemeIcon size={56} radius="xl" variant="light" color="gray">
                        <IconPlus size="1.6rem" stroke={2} />
                    </ThemeIcon>
                    <div style={{ textAlign: 'center' }}>
                        <Text fw={600}>Claim a Device</Text>
                        <Text size="sm" c="dimmed">
                            Add to the fleet
                        </Text>
                    </div>
                </UnstyledButton>
            </SimpleGrid>
        </div>
    );
};

export default Devices;
